/**
 * Monthly Report Page
 * 
 * รายงานรายเดือน - แสดงสถิติการเงินรายเดือน
 * ใช้ข้อมูลจริงจาก Parse Server (แบบ batch loading เหมือน Angular)
 */

import { useState, useEffect, useMemo } from 'react'
import moment from 'moment'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Calendar,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  Gift,
  Building2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/components/auth/auth-provider'
import { ReportService, type MonthlyReportData, type FinancialStatistics } from '@/domain/report'

// ============================================
// Constants
// ============================================

const MONTHS = [
  { value: 1, name: 'มกราคม' },
  { value: 2, name: 'กุมภาพันธ์' },
  { value: 3, name: 'มีนาคม' },
  { value: 4, name: 'เมษายน' },
  { value: 5, name: 'พฤษภาคม' },
  { value: 6, name: 'มิถุนายน' },
  { value: 7, name: 'กรกฎาคม' },
  { value: 8, name: 'สิงหาคม' },
  { value: 9, name: 'กันยายน' },
  { value: 10, name: 'ตุลาคม' },
  { value: 11, name: 'พฤศจิกายน' },
  { value: 12, name: 'ธันวาคม' },
]

const DAY_NAMES = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส']

// ============================================
// Helper Functions
// ============================================

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('th-TH', {
    style: 'currency',
    currency: 'THB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

function formatNumber(num: number): string {
  return new Intl.NumberFormat('th-TH').format(num)
}

function formatCompact(amount: number): string {
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`
  if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K`
  return formatNumber(amount)
}

function calculateChange(current: number, previous: number): { value: number; percentage: number; type: 'increase' | 'decrease' | 'neutral' } {
  if (previous === 0) {
    return { value: current, percentage: current > 0 ? 100 : 0, type: current > 0 ? 'increase' : 'neutral' }
  }
  
  const difference = current - previous
  const percentage = (difference / previous) * 100
  
  return {
    value: difference,
    percentage: Math.abs(percentage),
    type: difference > 0 ? 'increase' : difference < 0 ? 'decrease' : 'neutral',
  }
}

// ============================================
// Stat Card Component with Comparison
// ============================================

interface StatCardProps {
  label: string
  value: number
  previousValue?: number
  icon: React.ReactNode
  color: string
  bgColor: string
  formatValue?: (val: number) => string
}

function StatCard({ label, value, previousValue, icon, color, bgColor, formatValue = formatCurrency }: StatCardProps) {
  const change = previousValue !== undefined ? calculateChange(value, previousValue) : null
  
  return (
    <Card className="border-zinc-200/50 dark:border-zinc-800 hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={cn('p-2.5 rounded-xl', bgColor)}>
            <div className={cn('w-5 h-5', color)}>{icon}</div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground truncate">{label}</p>
            <p className={cn('text-xl font-bold mt-0.5', color)}>
              {formatValue(value)}
            </p>
            {change && change.type !== 'neutral' && (
              <div className="flex items-center gap-1 mt-1">
                {change.type === 'increase' ? (
                  <TrendingUp className="w-3 h-3 text-emerald-600" />
                ) : (
                  <TrendingDown className="w-3 h-3 text-rose-600" />
                )}
                <span
                  className={cn(
                    'text-xs font-medium',
                    change.type === 'increase' ? 'text-emerald-600' : 'text-rose-600'
                  )}
                >
                  {change.type === 'increase' ? '+' : '-'}
                  {change.percentage.toFixed(1)}%
                </span>
                <span className="text-xs text-muted-foreground">
                  ({change.type === 'increase' ? '+' : ''}
                  {formatCurrency(change.value)})
                </span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================
// Main Component
// ============================================

export function MonthlyReportPage() {
  const { sessionToken } = useAuth()
  
  // State
  const currentDate = moment()
  const [selectedYear, setSelectedYear] = useState(currentDate.year())
  const [selectedMonth, setSelectedMonth] = useState(currentDate.month() + 1) // 1-12
  const [reportData, setReportData] = useState<MonthlyReportData | null>(null)
  const [previousMonthData, setPreviousMonthData] = useState<FinancialStatistics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Available years (current year - 5 to current year + 1)
  const availableYears = useMemo(() => {
    const currentYear = moment().year()
    const years: number[] = []
    for (let year = currentYear - 5; year <= currentYear + 1; year++) {
      years.push(year)
    }
    return years
  }, [])
  
  // Load report data
  const loadReport = async (showLoading = true) => {
    if (!sessionToken) return
    
    console.log('🔄 [MonthlyReport] Loading report for:', {
      year: selectedYear,
      month: selectedMonth,
      monthName: MONTHS.find((m) => m.value === selectedMonth)?.name,
    })
    
    if (showLoading) setIsLoading(true)
    else setIsRefreshing(true)
    
    setError(null)
    
    // Clear old data immediately when changing month (เพื่อให้เห็น loading state)
    if (showLoading) {
      setReportData(null)
      setPreviousMonthData(null)
    }
    
    try {
      const [data, prevData] = await Promise.all([
        ReportService.getMonthlyReport(sessionToken, {
          year: selectedYear,
          month: selectedMonth,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
        ReportService.getPreviousMonthReport(sessionToken, {
          year: selectedYear,
          month: selectedMonth,
        }),
      ])
      
      console.log('✅ [MonthlyReport] Data loaded:', {
        year: selectedYear,
        month: selectedMonth,
        financial: {
          deposit: data.financial.totalDepositRegularAmount,
          withdraw: data.financial.totalWithdrawRegularAmount,
          profit: data.financial.netProfit,
        },
      })
      
      setReportData(data)
      setPreviousMonthData(prevData)
    } catch (err: any) {
      console.error('❌ [MonthlyReport] Failed to load:', err)
      setError(err.message || 'ไม่สามารถโหลดข้อมูลรายงานได้')
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }
  
  useEffect(() => {
    loadReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken, selectedYear, selectedMonth])
  
  // Debug: ตรวจสอบว่า reportData อัปเดตหรือไม่
  useEffect(() => {
    if (reportData) {
      console.log('📊 [MonthlyReport] Report data updated:', {
        year: selectedYear,
        month: selectedMonth,
        totalDeposit: reportData.financial.totalDepositRegularAmount,
        totalWithdraw: reportData.financial.totalWithdrawRegularAmount,
        netProfit: reportData.financial.netProfit,
      })
    }
  }, [reportData, selectedYear, selectedMonth])
  
  // Handlers
  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12)
      setSelectedYear(selectedYear - 1)
    } else {
      setSelectedMonth(selectedMonth - 1)
    }
  }
  
  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1)
      setSelectedYear(selectedYear + 1)
    } else {
      setSelectedMonth(selectedMonth + 1)
    }
  }
  
  const handleRefresh = () => loadReport(false)
  
  // Export CSV
  const handleExportCSV = () => {
    if (!reportData) return
    
    const csvData = reportData.dailyTable.map((row, index) => ({
      'ลำดับ': index + 1,
      'วันที่': row.displayDate,
      'ยอดฝากเงินสด (฿)': row.cashDeposit.toFixed(2),
      'ยอดถอนเงินสด (฿)': row.cashWithdraw.toFixed(2),
      'ยอดฝากโบนัส (฿)': row.bonusDeposit.toFixed(2),
      'คืนยอดเสีย (฿)': row.returnToMember.toFixed(2),
      'กระแสเงินสดสุทธิ (฿)': row.netCashFlow.toFixed(2),
      'กำไรสุทธิรวม (฿)': row.totalProfit.toFixed(2),
      'จำนวนรายการฝาก': row.depositCount,
      'จำนวนรายการถอน': row.withdrawCount,
      'จำนวนรายการทั้งหมด': row.totalTransactionCount,
      'ฝากโดยแอดมิน (฿)': row.adminDepositAmount.toFixed(2),
      'ฝากอัตโนมัติ (฿)': row.autoDepositAmount.toFixed(2),
      'ถอนโดยแอดมิน (฿)': row.adminWithdrawAmount.toFixed(2),
      'ถอนอัตโนมัติ (฿)': row.autoWithdrawAmount.toFixed(2),
    }))
    
    const headers = Object.keys(csvData[0])
    const csvContent = [
      headers.join(','),
      ...csvData.map((row) =>
        headers
          .map((header) => {
            const value = row[header as keyof typeof row]
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`
            }
            return value
          })
          .join(',')
      ),
    ].join('\n')
    
    const monthName = MONTHS.find((m) => m.value === selectedMonth)?.name || ''
    const filename = `monthly_summary_${monthName}_${selectedYear}.csv`
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  }
  
  // Find best and worst days (เฉพาะวันที่มีข้อมูล)
  const bestDay = useMemo(() => {
    if (!reportData || reportData.dailyTable.length === 0) return null
    const daysWithData = reportData.dailyTable.filter((d) => d.totalTransactionCount > 0)
    if (daysWithData.length === 0) return null
    return daysWithData.reduce((best, day) =>
      day.totalProfit > best.totalProfit ? day : best
    )
  }, [reportData])
  
  const worstDay = useMemo(() => {
    if (!reportData || reportData.dailyTable.length === 0) return null
    const daysWithData = reportData.dailyTable.filter((d) => d.totalTransactionCount > 0)
    if (daysWithData.length === 0) return null
    return daysWithData.reduce((worst, day) =>
      day.totalProfit < worst.totalProfit ? day : worst
    )
  }, [reportData])
  
  // Get calendar data for Mini Calendar
  const calendarData = useMemo(() => {
    if (!reportData) return []
    
    const startOfMonth = moment().year(selectedYear).month(selectedMonth - 1).startOf('month')
    const endOfMonth = moment().year(selectedYear).month(selectedMonth - 1).endOf('month')
    const today = moment().startOf('day')
    
    const days: Array<{
      date: number
      dayOfWeek: number
      profit: number
      hasData: boolean
      isToday: boolean
      isFuture: boolean
    }> = []
    
    let currentDate = startOfMonth.clone()
    while (currentDate.isSameOrBefore(endOfMonth, 'day')) {
      const dateStr = currentDate.format('YYYY-MM-DD')
      const dayData = reportData.dailyTable.find((d) => d.date === dateStr)
      
      days.push({
        date: currentDate.date(),
        dayOfWeek: currentDate.day(),
        profit: dayData?.totalProfit || 0,
        hasData: dayData ? dayData.totalTransactionCount > 0 : false,
        isToday: currentDate.isSame(today, 'day'),
        isFuture: currentDate.isAfter(today, 'day'),
      })
      
      currentDate.add(1, 'day')
    }
    
    return days
  }, [reportData, selectedYear, selectedMonth])
  
  const selectedMonthName = MONTHS.find((m) => m.value === selectedMonth)?.name || ''
  
  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">รายงานการเงินรายเดือน</h1>
          <p className="text-muted-foreground mt-1">
            สรุปยอดฝาก-ถอนรายวันในแต่ละเดือน
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Month Navigator */}
          <div className="flex items-center gap-1 bg-muted/30 rounded-lg p-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handlePrevMonth}
              disabled={isLoading}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <Select
              value={selectedYear.toString()}
              onValueChange={(v) => setSelectedYear(Number(v))}
            >
              <SelectTrigger className="w-[100px] h-8 border-0 bg-transparent">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select
              value={selectedMonth.toString()}
              onValueChange={(v) => setSelectedMonth(Number(v))}
            >
              <SelectTrigger className="w-[120px] h-8 border-0 bg-transparent">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((month) => (
                  <SelectItem key={month.value} value={month.value.toString()}>
                    {month.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleNextMonth}
              disabled={isLoading}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="h-8 w-8"
          >
            <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
          </Button>
          
          <Button
            variant="outline"
            className="gap-2 h-8"
            onClick={handleExportCSV}
            disabled={!reportData || isLoading}
          >
            <Download className="h-4 w-4" />
            ส่งออก CSV
          </Button>
        </div>
      </div>
      
      {/* Error State */}
      {error && (
        <Card className="border-rose-500/50 bg-rose-500/10">
          <CardContent className="p-4">
            <p className="text-rose-600">{error}</p>
          </CardContent>
        </Card>
      )}
      
      {/* Loading State */}
      {isLoading && !reportData ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : reportData ? (
        <>
          {/* Financial Stats Cards with Comparison */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5" key={`cards-${selectedYear}-${selectedMonth}`}>
            <StatCard
              label="ฝากเงินสด"
              value={reportData.financial.totalDepositRegularAmount}
              previousValue={previousMonthData?.totalDepositRegularAmount}
              icon={<ArrowDownToLine className="w-5 h-5" />}
              color="text-emerald-600"
              bgColor="bg-emerald-500/10"
            />
            
            <StatCard
              label="ถอนเงินสด"
              value={reportData.financial.totalWithdrawRegularAmount}
              previousValue={previousMonthData?.totalWithdrawRegularAmount}
              icon={<ArrowUpFromLine className="w-5 h-5" />}
              color="text-orange-600"
              bgColor="bg-orange-500/10"
            />
            
            <StatCard
              label="ฝากโบนัส"
              value={reportData.financial.totalDepositBonusAmount}
              previousValue={previousMonthData?.totalDepositBonusAmount}
              icon={<Gift className="w-5 h-5" />}
              color="text-amber-600"
              bgColor="bg-amber-500/10"
            />
            
            <StatCard
              label="กำไรสุทธิ"
              value={reportData.financial.netProfit}
              previousValue={previousMonthData?.netProfit}
              icon={<Wallet className="w-5 h-5" />}
              color={reportData.financial.netProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}
              bgColor={reportData.financial.netProfit >= 0 ? 'bg-emerald-500/10' : 'bg-rose-500/10'}
            />
            
            <StatCard
              label="ธุรกรรมทั้งหมด"
              value={reportData.financial.totalTransactions}
              icon={<Building2 className="w-5 h-5" />}
              color="text-violet-600"
              bgColor="bg-violet-500/10"
              formatValue={formatNumber}
            />
          </div>
          
          {/* Best/Worst Day */}
          {bestDay && worstDay && (
            <div className="grid gap-4 md:grid-cols-2">
              <Card className="border-emerald-500/30 bg-emerald-500/5">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-emerald-500/20">
                        <TrendingUp className="h-5 w-5 text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">วันที่กำไรสูงสุด</p>
                        <p className="font-semibold">{bestDay.displayDate}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-emerald-600">
                        +{formatCurrency(bestDay.totalProfit)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-rose-500/30 bg-rose-500/5">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-rose-500/20">
                        <TrendingDown className="h-5 w-5 text-rose-500" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">วันที่กำไรต่ำสุด</p>
                        <p className="font-semibold">{worstDay.displayDate}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className={cn(
                          'text-2xl font-bold',
                          worstDay.totalProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'
                        )}
                      >
                        {worstDay.totalProfit >= 0 ? '+' : ''}
                        {formatCurrency(worstDay.totalProfit)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Daily Table - แสดงเฉพาะวันที่ที่ผ่านมาแล้ว */}
          <Card className="border-zinc-200/50 dark:border-zinc-800 overflow-hidden">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                รายละเอียดรายวัน - {selectedMonthName} {selectedYear}
              </CardTitle>
              <CardDescription>
                แสดงยอดฝาก ถอน และกำไรของแต่ละวัน (เฉพาะวันที่ที่ผ่านมาแล้ว)
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-y">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground w-32">
                        วันที่
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">
                        ฝากเงินสด
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">
                        ถอนเงินสด
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">
                        ฝากโบนัส
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">
                        คืนยอดเสีย
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">
                        กำไรสุทธิ
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">
                        รายการ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.dailyTable.map((day) => {
                      const date = moment(day.date)
                      const dayOfWeek = date.day()
                      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
                      const isBestDay = bestDay && day.date === bestDay.date
                      const isWorstDay = worstDay && day.date === worstDay.date
                      const isToday = date.isSame(moment(), 'day')
                      
                      return (
                        <tr
                          key={day.date}
                          className={cn(
                            'border-b hover:bg-muted/30 transition-colors',
                            isWeekend && 'bg-muted/10',
                            isBestDay && 'bg-emerald-500/5',
                            isWorstDay && day.totalProfit < 0 && 'bg-rose-500/5',
                            isToday && 'ring-2 ring-blue-500/30'
                          )}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span
                                className={cn(
                                  'font-medium',
                                  isBestDay && 'text-emerald-600',
                                  isWorstDay && day.totalProfit < 0 && 'text-rose-600',
                                  isToday && 'text-blue-600 font-bold'
                                )}
                              >
                                {day.displayDate}
                              </span>
                              <Badge
                                variant="secondary"
                                className={cn(
                                  'text-xs border-0',
                                  isWeekend
                                    ? 'bg-blue-500/10 text-blue-500'
                                    : 'bg-muted',
                                  isToday && 'bg-blue-500/20 text-blue-600 font-bold'
                                )}
                              >
                                {DAY_NAMES[dayOfWeek]}
                              </Badge>
                              {isBestDay && (
                                <Badge
                                  variant="secondary"
                                  className="h-5 text-[10px] bg-emerald-500/10 text-emerald-600 border-0"
                                >
                                  สูงสุด
                                </Badge>
                              )}
                              {isWorstDay && day.totalProfit < 0 && (
                                <Badge
                                  variant="secondary"
                                  className="h-5 text-[10px] bg-rose-500/10 text-rose-600 border-0"
                                >
                                  ต่ำสุด
                                </Badge>
                              )}
                              {isToday && (
                                <Badge
                                  variant="secondary"
                                  className="h-5 text-[10px] bg-blue-500/10 text-blue-600 border-0"
                                >
                                  วันนี้
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-medium text-emerald-600">
                              {formatCurrency(day.cashDeposit)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-medium text-orange-600">
                              {formatCurrency(day.cashWithdraw)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-medium text-amber-600">
                              {formatCurrency(day.bonusDeposit)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-medium text-blue-600">
                              {formatCurrency(day.returnToMember)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span
                              className={cn(
                                'font-semibold',
                                day.totalProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'
                              )}
                            >
                              {day.totalProfit >= 0 ? '+' : ''}
                              {formatCurrency(day.totalProfit)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-muted-foreground">
                              {formatNumber(day.totalTransactionCount)}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot className="bg-muted/30 border-t-2">
                    <tr className="font-semibold">
                      <td className="px-4 py-4">
                        <span className="text-lg">รวมทั้งเดือน</span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-lg text-emerald-600">
                          {formatCurrency(reportData.financial.totalDepositRegularAmount)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-lg text-orange-600">
                          {formatCurrency(reportData.financial.totalWithdrawRegularAmount)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-lg text-amber-600">
                          {formatCurrency(reportData.financial.totalDepositBonusAmount)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-lg text-blue-600">
                          {formatCurrency(reportData.financial.totalReturnToMemberAmount)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span
                          className={cn(
                            'text-lg',
                            reportData.financial.netProfit >= 0
                              ? 'text-emerald-600'
                              : 'text-rose-600'
                          )}
                        >
                          {reportData.financial.netProfit >= 0 ? '+' : ''}
                          {formatCurrency(reportData.financial.netProfit)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-lg">
                          {formatNumber(reportData.financial.totalTransactions)}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
          
          {/* Mini Calendar View */}
          <Card className="border-zinc-200/50 dark:border-zinc-800">
            <CardHeader>
              <CardTitle className="text-base">ภาพรวมกำไรรายวัน</CardTitle>
              <CardDescription>
                สีเขียว = กำไร, สีแดง = ขาดทุน (ความเข้มตามยอด) • วงกลม = วันนี้
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1">
                {/* Day headers */}
                {DAY_NAMES.map((day) => (
                  <div
                    key={day}
                    className="text-center text-xs text-muted-foreground py-1 font-medium"
                  >
                    {day}
                  </div>
                ))}
                
                {/* Empty cells for days before the 1st */}
                {Array.from({
                  length: moment()
                    .year(selectedYear)
                    .month(selectedMonth - 1)
                    .startOf('month')
                    .day(),
                }).map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square" />
                ))}
                
                {/* Day cells */}
                {calendarData.map((day) => {
                  const maxProfit = Math.max(
                    ...calendarData.map((d) => Math.abs(d.profit)),
                    1
                  )
                  const intensity = maxProfit > 0 ? Math.abs(day.profit) / maxProfit : 0
                  const opacity = day.isFuture ? 0.1 : 0.2 + intensity * 0.6 // 0.2 to 0.8
                  
                  return (
                    <div
                      key={day.date}
                      className={cn(
                        'aspect-square rounded-md flex flex-col items-center justify-center text-xs cursor-pointer hover:ring-2 ring-primary/50 transition-all relative',
                        day.isFuture
                          ? 'bg-zinc-200/30 dark:bg-zinc-800/30'
                          : day.profit >= 0
                          ? 'bg-emerald-500'
                          : 'bg-rose-500',
                        bestDay && day.date === parseInt(bestDay.date.split('-')[2]) &&
                          !day.isFuture &&
                          'ring-2 ring-emerald-500',
                        worstDay && day.date === parseInt(worstDay.date.split('-')[2]) &&
                          day.profit < 0 &&
                          !day.isFuture &&
                          'ring-2 ring-rose-500',
                        day.isToday && 'ring-2 ring-blue-500 ring-offset-2'
                      )}
                      style={{ opacity: day.isFuture ? 0.3 : opacity }}
                      title={
                        day.isFuture
                          ? `${day.date} ${selectedMonthName} (ยังไม่ถึง)`
                          : `${day.date} ${selectedMonthName}: ${formatCurrency(day.profit)}`
                      }
                    >
                      <span
                        className={cn(
                          'font-semibold',
                          day.isFuture ? 'text-muted-foreground' : 'text-white'
                        )}
                      >
                        {day.date}
                      </span>
                      {!day.isFuture && (
                        <span className="text-[10px] text-white/80">
                          {formatCompact(day.profit)}
                        </span>
                      )}
                      {day.isToday && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-background" />
                      )}
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  )
}

export default MonthlyReportPage
